import { useState, useMemo, useEffect, useCallback } from "react";
import { DateHelper } from "@bryntum/schedulerpro";
import { Skeleton } from "@/modern/components/ui/skeleton";
import { Button } from "@/modern/components/ui/button";
import { useEntityFileContext } from "@/react/components/entityViews/files/context/EntityFileContext";
import type { SwivuFile } from "@/react/components/entityViews/files/types/swivuFile";
import { PresignedFilePreview } from "@/react/components/entityViews/files/components/PresignedFilePreview";
import InfiniteScroll from "@/modern/components/infinite-scroll";
import { Loader2 } from "lucide-react";
import { FileActionsMenu } from "@/react/components/entityViews/files/components/FileActionsMenu";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { useFileSpotIncentive } from "@/react/components/entityViews/files/hooks/useFileSpotIncentive";
import { useEntityFileUpload } from "@/react/components/entityViews/files/hooks/useEntityFileUpload";
import { PERSISTENCE_STATE_KEYS } from "@/other/constants/PersistenceStateKeys";
import type { FileViewerMode } from "./FileDetailModal/types";
import {
  FileGridToolbar,
  type FileBrowserFilterState,
  type FileTypeTab,
} from "./FileGridToolbar";

type ViewMode = "grid" | "list";

function parseFileBrowserViewMode(value: string | undefined | null): ViewMode {
  return value === "list" ? "list" : "grid";
}

/** Cap zoom so Largest is still a multi-cell density, not a fake single-image viewer. */
const ZOOM_LEVELS = [
  { cols: 6, label: "Smallest" },
  { cols: 5, label: "Small" },
  { cols: 4, label: "Medium" },
  { cols: 3, label: "Large" },
  { cols: 2, label: "Larger" },
];

const formatFileSize = (bytes: number | null): string => {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

interface FileGridViewProps {
  /** Controlled browser filters (TaskFilesEditor). When set, list is server-filtered. */
  filters?: FileBrowserFilterState;
  onFiltersChange?: (next: FileBrowserFilterState) => void;
  onSearchChange?: (search: string) => void;
  /** @deprecated Prefer `filters.fileTypeTab` — kept for callers that only drive type tab */
  activeFilter?: FileTypeTab;
  onFilterChange?: (f: FileTypeTab) => void;
  onNavigationFilesChange?: (files: SwivuFile[]) => void;
}

export const FileGridView = ({
  filters: controlledFilters,
  onFiltersChange,
  onSearchChange,
  activeFilter: legacyFilter,
  onFilterChange,
  onNavigationFilesChange,
}: FileGridViewProps = {}) => {
  const {
    files,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoadingFiles,
    isErrorFiles,
    refetch,
    handleFileOpen,
    handleFileDownload,
    deleteFile,
    entityRecord,
    uploadFiles,
    isImportingCompanyCam,
    currentCCProject,
  } = useEntityFileContext();

  const { project, editorHandler, userPreferences } = useSwivuBryntumContext();

  const [internalFilters, setInternalFilters] = useState<FileBrowserFilterState>({
    fileTypeTab: legacyFilter ?? "media",
    source: "all",
    datePreset: "any",
    search: "",
    ordering: "-uploaded_at",
  });

  const isControlled = controlledFilters !== undefined;
  const filters = isControlled ? controlledFilters : internalFilters;

  const setFilters = useCallback(
    (next: FileBrowserFilterState) => {
      if (isControlled) {
        onFiltersChange?.(next);
        onFilterChange?.(next.fileTypeTab);
      } else {
        setInternalFilters(next);
      }
    },
    [isControlled, onFiltersChange, onFilterChange],
  );

  const setSearch = useCallback(
    (search: string) => {
      if (onSearchChange) {
        onSearchChange(search);
        return;
      }
      setFilters({ ...filters, search });
    },
    [onSearchChange, setFilters, filters],
  );

  const [viewMode, setViewMode] = useState<ViewMode>(() =>
    parseFileBrowserViewMode(
      userPreferences?.getSetting(PERSISTENCE_STATE_KEYS.fileBrowserViewMode, "grid"),
    ),
  );

  const handleViewModeChange = useCallback(
    (mode: ViewMode) => {
      setViewMode(mode);
      userPreferences?.updateSetting(PERSISTENCE_STATE_KEYS.fileBrowserViewMode, mode);
    },
    [userPreferences],
  );

  const [zoomLevel, setZoomLevel] = useState<number>(() => {
    const stored = localStorage.getItem("fileGridZoomLevel");
    const parsed = stored ? parseInt(stored, 10) : 1;
    return Math.min(Math.max(parsed, 0), ZOOM_LEVELS.length - 1);
  });

  const uploadCategory = filters.fileTypeTab === "documents" ? "documents" : "media";
  const { uploadRef, isUploading, openUpload, handleUpload, accept } = useEntityFileUpload({
    category: uploadCategory,
    uploadNewFile: uploadFiles,
  });

  const currentCols = ZOOM_LEVELS[zoomLevel].cols;

  const companyCamProjectId = entityRecord.companyCamProject as number | undefined;
  const companyCamName =
    currentCCProject?.name ??
    (companyCamProjectId ? `Project ${companyCamProjectId}` : null);

  const companyCamImportHint =
    filters.fileTypeTab === "documents"
      ? "Documents will appear here shortly."
      : filters.fileTypeTab === "media"
        ? "Media will appear here shortly."
        : "Files will appear here shortly.";

  const { shouldShowAdjustPoints } = useFileSpotIncentive({
    entityRecord,
    project,
    editorHandler,
  });

  // Controlled path trusts server filters. Uncontrolled path still client-filters by type
  // (source/date/search/ordering require provider remount via controlled TaskFilesEditor).
  const filteredFiles = useMemo(() => {
    if (isControlled) return files;
    switch (filters.fileTypeTab) {
      case "media":
        return files.filter((f) => f.fileType === "image");
      case "documents":
        return files.filter((f) => f.fileType === "document");
      default:
        return files;
    }
  }, [isControlled, filters.fileTypeTab, files]);

  useEffect(() => {
    onNavigationFilesChange?.(filteredFiles);
  }, [filteredFiles, onNavigationFilesChange]);

  const openWithMode = useCallback(
    (file: SwivuFile, mode: FileViewerMode = "default") => {
      handleFileOpen(file, { mode });
    },
    [handleFileOpen],
  );

  const zoomIn = () => {
    if (zoomLevel < ZOOM_LEVELS.length - 1) {
      const newLevel = zoomLevel + 1;
      setZoomLevel(newLevel);
      localStorage.setItem("fileGridZoomLevel", newLevel.toString());
    }
  };

  const zoomOut = () => {
    if (zoomLevel > 0) {
      const newLevel = zoomLevel - 1;
      setZoomLevel(newLevel);
      localStorage.setItem("fileGridZoomLevel", newLevel.toString());
    }
  };

  const renderActions = (file: SwivuFile) => (
    <FileActionsMenu
      file={file}
      onFileDownload={handleFileDownload}
      onFileDelete={(fileId) => deleteFile(fileId)}
      onView={(f) => openWithMode(f)}
      onAnnotate={(f) => openWithMode(f, "annotate")}
      onAnalyze={(f) => openWithMode(f, "analyze")}
      onPromote={(f) => openWithMode(f, "promote")}
      onAdjustPoints={(f) => openWithMode(f, "adjustPoints")}
      onSummaryShare={(f) => openWithMode(f, "summaryShare")}
      onExtract={(f) => openWithMode(f, "extract")}
      shouldShowAdjustPoints={shouldShowAdjustPoints}
    />
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <FileGridToolbar
        filters={filters}
        onFiltersChange={setFilters}
        onSearchChange={setSearch}
        viewMode={viewMode}
        onViewModeChange={handleViewModeChange}
        zoomLevel={zoomLevel}
        zoomMax={ZOOM_LEVELS.length - 1}
        onZoomIn={zoomIn}
        onZoomOut={zoomOut}
        isUploading={isUploading}
        onUpload={openUpload}
        uploadAcceptLabel={filters.fileTypeTab === "documents" ? "documents" : "media"}
        uploadRef={uploadRef}
        accept={accept}
        onUploadChange={handleUpload}
      />

      <div className="min-h-0 flex-1 overflow-y-auto">
      {isLoadingFiles && filteredFiles.length === 0 && (
        <div className="grid grid-cols-3 gap-3">
          {Array.from({ length: 6 }).map((_, index) => (
            <div key={index} className="space-y-2">
              <Skeleton className="h-32 w-full rounded-2xl" />
              <Skeleton className="h-4 w-3/4 rounded-lg" />
            </div>
          ))}
        </div>
      )}

      {isErrorFiles && (
        <div className="flex flex-col items-center justify-center gap-3 py-10 text-center">
          <p className="text-sm font-normal text-muted-foreground">Failed to load files.</p>
          <Button size="sm" variant="outline" className="rounded-full font-normal" onClick={() => refetch()}>
            Retry
          </Button>
        </div>
      )}

      {!isLoadingFiles && !isErrorFiles && filteredFiles.length === 0 && (
        isImportingCompanyCam ? (
          <div className="flex flex-col items-center justify-center gap-1.5 px-6 py-10 text-center">
            <div className="flex max-w-lg items-center justify-center gap-2 text-sm font-normal text-muted-foreground">
              <Loader2 className="size-4 shrink-0 animate-spin opacity-80" aria-hidden />
              <span className="min-w-0">
                {companyCamName
                  ? `Importing from CompanyCam project ${companyCamName}…`
                  : "Importing from CompanyCam project…"}
              </span>
            </div>
            <p className="text-xs font-normal text-muted-foreground/70">{companyCamImportHint}</p>
          </div>
        ) : (
          <div className="py-10 text-center text-sm font-normal text-muted-foreground">No files found</div>
        )
      )}

      {filteredFiles.length > 0 && (
        <>
          {viewMode === "grid" ? (
            <InfiniteScroll hasMore={hasNextPage} isLoading={isFetchingNextPage} next={fetchNextPage} threshold={1}>
              <div
                className="grid gap-3"
                style={{ gridTemplateColumns: `repeat(${currentCols}, minmax(0, 1fr))` }}
              >
                {filteredFiles.map((file) => (
                  <div key={file.id} className="group relative">
                    <div
                      onClick={() => openWithMode(file)}
                      className="block cursor-pointer overflow-hidden rounded-2xl border border-border/60 bg-muted/20 transition-[box-shadow,border-color] duration-200 ease-out hover:border-border"
                    >
                      <div className="relative aspect-square w-full overflow-hidden">
                        <PresignedFilePreview
                          file={file}
                          className="absolute inset-0 size-full object-cover"
                          fileIconClassName="size-16"
                          fileIconContainerClassName="absolute inset-0 size-full"
                          compactLoading
                          isThumbnail
                        />
                      </div>

                      <div className="absolute inset-0 flex flex-col justify-between rounded-2xl bg-black/70 p-3 opacity-0 transition-opacity duration-200 group-hover:opacity-100">
                        <div className="flex justify-end">{renderActions(file)}</div>
                        <div className="truncate text-sm font-normal text-white" title={file.name}>
                          {file.name}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {(isFetchingNextPage || hasNextPage) && (
                <div className="flex justify-center py-4">
                  {isFetchingNextPage && (
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  )}
                </div>
              )}
            </InfiniteScroll>
          ) : (
            <div className="space-y-2">
              <InfiniteScroll hasMore={hasNextPage} isLoading={isFetchingNextPage} next={fetchNextPage} threshold={1}>
                {filteredFiles.map((file) => (
                  <button
                    key={file.id}
                    type="button"
                    onClick={() => openWithMode(file)}
                    className="group flex w-full items-center gap-3 rounded-2xl border border-border/60 bg-muted/20 p-3 text-left transition-colors duration-150 ease-out hover:bg-muted/40"
                  >
                    <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center overflow-hidden rounded-xl">
                      <PresignedFilePreview
                        file={file}
                        className="h-12 w-12 object-cover"
                        fileIconClassName="size-6"
                        fileIconContainerClassName="h-12 w-12"
                        compactLoading
                        isThumbnail
                        showLoadingLabel={false}
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p
                        className="truncate text-sm font-normal text-foreground"
                        title={file.name}
                      >
                        {file.name || "Untitled"}
                      </p>
                      <div className="mt-1 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                        {file.sizeBytes && <span>{formatFileSize(file.sizeBytes)}</span>}
                        {file.createdAt && (
                          <span>{DateHelper.format(new Date(file.createdAt), "MM/DD/YYYY")}</span>
                        )}
                        {file.source === "companycam" && (
                          <span className="flex items-center gap-1">
                            <i className="fa-solid fa-camera text-[10px]" />
                            CompanyCam
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex-shrink-0 opacity-0 transition-opacity group-hover:opacity-100">
                      {renderActions(file)}
                    </div>
                  </button>
                ))}
                {(isFetchingNextPage || hasNextPage) && (
                  <div className="flex justify-center py-8">
                    {isFetchingNextPage && (
                      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                    )}
                  </div>
                )}
              </InfiniteScroll>
            </div>
          )}
        </>
      )}
      </div>
    </div>
  );
};
